"use client"

import type { Dispatch, SetStateAction } from "react"
import { useTranslations } from "next-intl"
import { toast } from "sonner"
import type { BooruPost } from "@/lib/booru-api"
import { extractSimilarTags } from "@/lib/similar-finder"

// Moved out of components/booru-detail-modal.tsx (same functions).
export function useModalPostActions({
  post,
  allPosts,
  toggleFavorite,
  onOpenTagInNewSet,
  onTagsAdd,
  onTagClick,
  setIsFindSimilarDropdownOpen,
}: {
  post: BooruPost | null
  allPosts: BooruPost[]
  toggleFavorite: (post: BooruPost) => boolean
  onOpenTagInNewSet: ((tag: string) => void) | undefined
  onTagsAdd: ((tags: string[]) => void) | undefined
  onTagClick: ((tag: string) => void) | undefined
  setIsFindSimilarDropdownOpen: Dispatch<SetStateAction<boolean>>
}) {
  const tt = useTranslations("booruDetailModal")

  const handleFavoriteClick = () => {
    if (post) {
      toggleFavorite(post)
    }
  }

  const handleShare = async () => {
    if (!post) return
    try {
      if (navigator.share) {
        try {
          await navigator.share({
            title: `Post ${post.id}`,
            url: post.fileUrl,
          })
        } catch (shareError) {
          if (shareError instanceof Error && shareError.name !== "AbortError") {
            await navigator.clipboard.writeText(post.fileUrl)
            toast.success(tt("linkCopiedToClipboard"))
          }
        }
      } else {
        await navigator.clipboard.writeText(post.fileUrl)
        toast.success(tt("linkCopiedToClipboard"))
      }
    } catch (error) {
      console.error("Failed to share:", error)
      toast.error(tt("failedToCopyLinkToClipboard"))
    }
  }

  const handleFindSimilar = (action: "current" | "new") => {
    if (!post || !onOpenTagInNewSet) return

    const similarTags = extractSimilarTags(post, allPosts)
    if (similarTags.length === 0) return

    // For new set, create with all similar tags
    if (action === "new") {
      const allTagsStr = similarTags.join("+")
      onOpenTagInNewSet(allTagsStr)
    } else {
      // For current set, add tags one by one (existing behavior)
      if (onTagsAdd) onTagsAdd(similarTags)
      else similarTags.forEach((tag) => onTagClick?.(tag))
    }

    setIsFindSimilarDropdownOpen(false)
  }

  return { handleFavoriteClick, handleShare, handleFindSimilar }
}
